import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useKlaviyoCRUDActions } from '@mm/state/actions/KlaviyoCRUDActions';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { selectKlaviyoConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectKlaviyoSuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useGuardedConnectOAuthToken } from '@mm/stateful-components/OAuth/use-guarded-connect-o-auth-token';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

export const prepareConnectionsMessage = (
  connections: ConnectionModel[],
  platformName: string,
  withList?: boolean,
): string | null => {
  if (connections.every(conn => !conn.active)) {
    return `The connection is inactive. Toggle it on to start syncing contacts to ${platformName}`;
  }
  const activeConnections = connections.filter(conn => conn.active);
  if (withList && activeConnections.every(conn => !conn.data.listId)) {
    return `Please choose ${platformName} list below to activate connection`;
  }

  return null;
};

export const useConnectKlaviyo = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const accounts = useSelector((state: StoreState) => state.klaviyo.accounts);
  const apiKeys = useSelector((state: StoreState) => state.klaviyo.apiKeys);
  const klaviyoConnections = useSelector(selectKlaviyoConnections);
  const klaviyoSuppressions = useSelector(selectKlaviyoSuppressionList);

  const {
    getOauthUrl,
    getAccounts,
    getApiKeys,
    getLists,
    getEvents,
    getRevenues,
    revoke: revokeToken,
    createApiKey,
    updateApiKey,
  } = useKlaviyoCRUDActions();
  const {
    retrieveAll: getSuppressions,
    create: createSuppression,
    update: updateSuppression,
  } = useSuppressionListsCRUDActions();
  const { retrieveAll, update, create } = useConnectionCRUDActions();

  const fetchOAuthUrl = useCallback(
    async (tokenId?: number) => {
      const link = await getOauthUrl(tokenId);
      return link;
    },
    [getOauthUrl],
  );

  const connectionsMessage = prepareConnectionsMessage(
    klaviyoConnections,
    'Klaviyo',
    true,
  );

  const updateKey = async (id: number, privateKey: string) => {
    await updateApiKey(id, privateKey);
    getLists(facebookPageId, id, 'api_key', true);
  };

  const createKey = async (privateKey: string) => {
    const { id } = await createApiKey(privateKey);
    getLists(facebookPageId, id, 'api_key', true);
    if (!klaviyoSuppressions.length) {
      createSuppression(facebookPageId, {
        enabled: false,
        sourceId: id,
        sourceKind: 'klaviyo_api_key',
      });
    } else if ((klaviyoSuppressions || []).find(supp => supp.sourceId !== id)) {
      klaviyoSuppressions.forEach(suppression => {
        updateSuppression(facebookPageId, {
          id: suppression.id,
          sourceId: id,
          sourceKind: 'klaviyo_api_key',
        });
      });
    }
    if (!klaviyoConnections.length) {
      create(facebookPageId, {
        name: 'Klaviyo Connection',
        connectionKind: 'klaviyo',
        klaviyoParams: { accessTokenId: id },
      });
    } else if (
      (klaviyoConnections || []).find(
        conn => conn.klaviyoAccessToken?.id !== id,
      )
    ) {
      klaviyoConnections.forEach(connection => {
        update(facebookPageId, {
          id: connection.id,
          klaviyoParams: { accessTokenId: id },
        });
      });
    }
  };

  const { connect: connectKlaviyoToken } = useGuardedConnectOAuthToken(
    'nativeIntegrations',
    landmineNames.KLAVIYO_UPGRADE,
    'klaviyoConnect',
    'klaviyoTokenReceived',
    fetchOAuthUrl,
    async () => {
      const accountsResponse = await getKlaviyoAccounts();
      const { id } = accountsResponse[0];
      getLists(facebookPageId, id, 'oauth_token', true);
      if (!klaviyoSuppressions.length) {
        createSuppression(facebookPageId, {
          enabled: false,
          sourceId: id,
          sourceKind: 'klaviyo_oauth',
        });
      } else if (
        (klaviyoSuppressions || []).find(conn => conn.sourceId !== id)
      ) {
        klaviyoSuppressions.forEach(suppression => {
          updateSuppression(facebookPageId, {
            id: suppression.id,
            sourceId: id,
            sourceKind: 'klaviyo_oauth',
          });
        });
      }
      if (!klaviyoConnections.length) {
        create(facebookPageId, {
          name: 'Klaviyo Connection',
          connectionKind: 'klaviyo',
          klaviyoParams: { oauthTokenId: id },
        });
      } else if (
        (klaviyoConnections || []).find(
          conn => conn.klaviyoOauthToken?.id !== id,
        )
      ) {
        klaviyoConnections.forEach(connection => {
          update(facebookPageId, {
            id: connection.id,
            klaviyoParams: { oauthTokenId: id },
          });
        });
      }
    },
    { width: 600, height: 600 },
  );

  const getKlaviyoAccounts = async () => getAccounts(facebookPageId);
  const getKlaviyoApiKeys = async () => getApiKeys(facebookPageId);

  const getKlaviyoConnections = () => {
    retrieveAll(facebookPageId, {
      type: 'klaviyo',
      pageSize: 200,
    });
  };

  const revoke = async (id: number) => {
    const result = await revokeToken(id);
    if (result) {
      getKlaviyoConnections();
      getSuppressions(facebookPageId);
    }
  };

  const refreshConnections = async () => {
    retrieveAll(facebookPageId, {
      type: 'klaviyo',
      pageSize: 200,
    });
  };

  return {
    accounts,
    apiKeys,
    connectionsMessage,
    getRevenues,
    getEvents,
    getLists,
    getKlaviyoAccounts,
    getKlaviyoApiKeys,
    refreshConnections,
    klaviyoConnections,
    getKlaviyoConnections,
    connectKlaviyoToken,
    revoke,
    createApiKey: createKey,
    updateApiKey: updateKey,
  };
};
